<template>
  <div
    class="slot-card-modern"
    :class="{ 'slot-unavailable': !slot.isAvailable }"
  >
    <div class="slot-header">
      <div class="slot-time">
        <svg class="time-icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
          <circle cx="9" cy="9" r="7.5" stroke="currentColor" stroke-width="1.5"/>
          <path d="M9 5V9L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        <strong>{{ formatTime(slot.startTime) }}</strong>
      </div>
      <span v-if="slot.isAvailable" class="slot-badge available">{{ $t("experiences.available") || "Disponivel" }}</span>
      <span v-else class="slot-badge unavailable">{{ $t("experiences.unavailableLabel") || "Indisponivel" }}</span>
    </div>

    <div class="slot-details">
      <div class="slot-detail-item">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 8C9.10457 8 10 7.10457 10 6C10 4.89543 9.10457 4 8 4C6.89543 4 6 4.89543 6 6C6 7.10457 6.89543 8 8 8Z" fill="currentColor"/>
          <path d="M8 9C6.34315 9 5 10.3431 5 12V13H11V12C11 10.3431 9.65685 9 8 9Z" fill="currentColor"/>
        </svg>
        <span v-if="slot.availableSpots !== undefined && slot.availableSpots < slot.maxPeople">
          {{ slot.availableSpots }}/{{ slot.maxPeople }} {{ $t("experiences.spotsAvailable") || "lugares disponíveis" }}
        </span>
        <span v-else>{{ slot.minPeople }}-{{ slot.maxPeople }} {{ $t("experiences.maxPeople") }}</span>
      </div>
      <div v-if="slot.guide && slot.guide.name" class="slot-detail-item">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0ZM8 2C11.3137 2 14 4.68629 14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2Z" fill="currentColor"/>
          <path d="M8 4C6.89543 4 6 4.89543 6 6C6 7.10457 6.89543 8 8 8C9.10457 8 10 7.10457 10 6C10 4.89543 9.10457 4 8 4Z" fill="currentColor"/>
          <path d="M8 9C6.34315 9 5 10.3431 5 12V13H11V12C11 10.3431 9.65685 9 8 9Z" fill="currentColor"/>
        </svg>
        <span><strong>{{ $t("experiences.guide") }}:</strong> {{ slot.guide.name }}</span>
      </div>
    </div>

    <!-- Per-person: every bracket's price for this date, so a senior or a child knows what they pay -->
    <div v-if="showPrice && bracketPrices.length" class="slot-bracket-prices">
      <span v-for="bracket in bracketPrices" :key="bracket.key" class="slot-bracket-price">
        {{ $t(`reservation.ageBrackets.${bracket.key.toLowerCase()}`) }}
        <strong>{{ bracket.price > 0 ? formatPrice(bracket.price) : $t('experiences.free') }}</strong>
      </span>
    </div>
    <div v-else-if="showPrice && slot.displayPrice > 0" class="slot-detail-item slot-price">
      <strong>{{ formatPrice(slot.displayPrice) }}</strong>
    </div>

    <div v-if="!slot.isAvailable" class="slot-warning">
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M9 6V10M9 14H9.01M9 2C5.13401 2 2 5.13401 2 9C2 12.866 5.13401 16 9 16C12.866 16 16 12.866 16 9C16 5.13401 12.866 2 9 2Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>
      <span v-if="slot.unavailabilityReason === 'archived'">{{ $t("experiences.archivedSlot") }}</span>
      <span v-else-if="slot.unavailabilityReason === 'past'">{{ $t("experiences.timePassed") }}</span>
      <span v-else-if="slot.unavailabilityReason === 'capacity'">{{ $t("experiences.capacityExceeded", { max: slot.maxPeople }) }}</span>
      <span v-else-if="slot.unavailabilityReason === 'full'">{{ $t("experiences.slotFull") || "Sem lugares disponíveis" }}</span>
      <span v-else>{{ $t("experiences.unavailableSlot") }}</span>
    </div>

    <button
      @click="$emit('select', slot)"
      class="slot-button"
      :class="slot.isAvailable ? 'slot-button-primary' : 'slot-button-disabled'"
      :disabled="!slot.isAvailable"
    >
      {{ slot.isAvailable ? $t("experiences.select") : $t("experiences.unavailable") }}
    </button>
  </div>
</template>

<script>
import { pricedAgeBrackets, formatEuro } from '@/helpers/agePrices.js';

export default {
  name: 'SlotCard',
  props: {
    slot: {
      type: Object,
      required: true
    },
    showPrice: {
      type: Boolean,
      default: false
    },
    priceType: {
      type: String,
      default: 'PerExperience'
    }
  },
  computed: {
    bracketPrices() {
      return this.priceType === 'PerPerson' ? pricedAgeBrackets(this.slot.ageBracketPrices) : [];
    }
  },
  methods: {
    formatPrice(value) {
      return formatEuro(value, this.$i18n.locale);
    },
    formatTime(time) {
      if (!time) return '';
      return time.substring(0, 5);
    }
  }
};
</script>

<style scoped>
.slot-card-modern {
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.slot-card-modern:hover {
  border-color: var(--primary-color);
  box-shadow: 0 8px 24px rgba(177, 209, 160, 0.15);
  transform: translateY(-2px);
}

.slot-card-modern.slot-unavailable {
  opacity: 0.7;
  background: #f9fafb;
}

.slot-card-modern.slot-unavailable:hover {
  border-color: #e5e7eb;
  box-shadow: none;
  transform: none;
}

.slot-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.slot-time {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  color: var(--secondary-color);
}

.time-icon {
  color: var(--primary-color);
  flex-shrink: 0;
}

.slot-badge {
  padding: 0.375rem 0.875rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.slot-badge.available {
  background: rgba(177, 209, 160, 0.15);
  color: #4a7c59;
}

.slot-badge.unavailable {
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
}

.slot-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f3f4f6;
}

.slot-detail-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
}

.slot-detail-item svg {
  color: var(--primary-color);
  flex-shrink: 0;
}

.slot-bracket-prices {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem 1rem;
  font-size: 0.875rem;
  color: #6b7280;
}

.slot-bracket-price strong {
  color: var(--secondary-color);
  margin-left: 0.25rem;
}

.slot-warning {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #fef2f2;
  border-radius: 8px;
  color: #dc2626;
  font-size: 0.875rem;
  font-weight: 500;
}

.slot-warning svg {
  flex-shrink: 0;
}

.slot-button {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: auto;
}

.slot-button-primary {
  background: var(--primary-color);
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(177, 209, 160, 0.3);
}

.slot-button-primary:hover:not(:disabled) {
  background: #9fc48a;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(177, 209, 160, 0.4);
}

.slot-button-disabled {
  background: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .slot-card-modern {
    padding: 1.25rem;
  }

  .slot-header {
    flex-wrap: wrap;
  }

  .slot-time {
    font-size: 1rem;
  }
}
</style>
